import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Chip,
    Button,
    Collapse,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { logout } from '../../slices/authSlice';
import ACCOUNT_PLANS from '../../config/accountPlans';
import { getProjectStatus } from '../../utils/projectStatus';

import { PLAN_CHIP, DASHBOARD_COLORS, SECTION_GAP } from './dashboardTheme';
import RecentProjectCard from './RecentProjectCard';
import KpiCard from './KpiCard';
import UsageCards from './UsageCards';
import AlertsStrip from './AlertsStrip';
import QuickActionsMenu from './QuickActionsMenu';
import ActivityChart from './ActivityChart';
import TopLanguagesChart from './TopLanguagesChart';

const UNLIMITED = 9007199254740991;

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
};

const computeMonthDelta = (wordsByDay) => {
    if (!Array.isArray(wordsByDay) || wordsByDay.length < 2) return null;
    const mid = Math.floor(wordsByDay.length / 2);
    const prev = wordsByDay.slice(0, mid).reduce((s, d) => s + (d.words || 0), 0);
    const curr = wordsByDay.slice(mid).reduce((s, d) => s + (d.words || 0), 0);
    if (prev === 0) return null;
    return ((curr - prev) / prev) * 100;
};

const DashboardHome = () => {
    const [activeProjects, setActiveProjects] = useState(0);
    const [completedProjects, setCompletedProjects] = useState(0);
    const [recentProjects, setRecentProjects] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [wordsTranslated, setWordsTranslated] = useState(0);
    const [wordsByDay, setWordsByDay] = useState([]);
    const [topLanguages, setTopLanguages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [usage, setUsage] = useState({ character_count: 0, storage_used: 0 });
    const [user, setUser] = useState({});
    const [team, setTeam] = useState([]);
    const [pendingInvites, setPendingInvites] = useState(0);
    const [checklistOpen, setChecklistOpen] = useState(true);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);

    const accountType = (user.accountType || 'essential').toLowerCase();
    const chipProps = PLAN_CHIP[accountType] || PLAN_CHIP.essential;
    const currentPlan = ACCOUNT_PLANS[accountType] || ACCOUNT_PLANS.essential;
    const charLimit = currentPlan.charLimit;
    const storageLimit = currentPlan.storageLimit;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const headers = { Authorization: `Bearer ${token}` };

                const [projectsRes, statsRes, userRes, teamRes] = await Promise.all([
                    axios.get('/api/projects?summary=true', { headers }),
                    axios.get('/api/stats', { headers }),
                    axios.get('/api/users/me', { headers }),
                    axios.get('/api/team', { headers }),
                ]);

                const { createdProjects = [], sharedProjects = [] } = projectsRes.data;
                const combined = [...createdProjects, ...sharedProjects];
                const uniqueProjects = Array.from(new Map(combined.map((p) => [p._id, p])).values());

                setAllProjects(uniqueProjects);
                setActiveProjects(uniqueProjects.filter((p) => !p.completed).length);
                setCompletedProjects(uniqueProjects.filter((p) => p.completed).length);
                setRecentProjects(
                    uniqueProjects
                        .slice()
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .slice(0, 3),
                );

                const { wordsTranslated: wt, topLanguages: tl, wordsByDay: wbd } = statsRes.data;
                setWordsTranslated(wt || 0);
                setTopLanguages(tl || []);
                setWordsByDay(Array.isArray(wbd) ? wbd : []);

                setUsage({
                    character_count: userRes.data.deeplUsage || 0,
                    storage_used: userRes.data.storageUsed || 0,
                });
                setUser(userRes.data);

                const teamData = teamRes.data || [];
                setTeam(teamData);
                setPendingInvites(
                    teamData.filter((m) => m && m.invitationStatus === 'pending').length,
                );

                setLoading(false);
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load dashboard data.');
                    setLoading(false);
                }
            }
        };
        fetchData();
    }, [token, navigate, dispatch]);

    const overdueCount = useMemo(() => {
        const now = Date.now();
        return allProjects.filter((p) => {
            if (!p.deadline || p.completed) return false;
            return new Date(p.deadline).getTime() < now && getProjectStatus(p) !== 'completed';
        }).length;
    }, [allProjects]);

    const mtPercent = charLimit > 0 && charLimit < UNLIMITED
        ? Math.min((usage.character_count / charLimit) * 100, 100)
        : 0;
    const storagePercent = storageLimit > 0 && storageLimit < UNLIMITED
        ? Math.min((usage.storage_used / storageLimit) * 100, 100)
        : 0;

    const isProfileComplete =
        !!user.firstName && !!user.lastName && !!user.occupation && !!user.email;
    const hasCreatedProject = activeProjects + completedProjects > 0;
    const hasTeamMembers = team && team.length > 1;

    const steps = [
        {
            label: 'Finish your profile information',
            icon: <PersonIcon color={isProfileComplete ? 'success' : 'disabled'} />,
            complete: isProfileComplete,
            action: () => navigate('/dashboard/profile'),
        },
        {
            label: 'Create your first project',
            icon: <AddCircleIcon color={hasCreatedProject ? 'success' : 'disabled'} />,
            complete: hasCreatedProject,
            action: () => navigate('/dashboard/projects/new'),
        },
    ];
    if (accountType !== 'essential') {
        steps.push({
            label: 'Add team members',
            icon: <GroupAddIcon color={hasTeamMembers ? 'success' : 'disabled'} />,
            complete: hasTeamMembers,
            action: () => navigate('/dashboard/team'),
        });
    }
    const allComplete = steps.every((s) => s.complete);

    const monthDelta = computeMonthDelta(wordsByDay);
    const monthDeltaSubtext =
        monthDelta === null
            ? 'this month'
            : `${monthDelta >= 0 ? '+' : ''}${monthDelta.toFixed(0)}% vs prior period`;

    if (loading) return <Typography variant="h5" sx={{ p: 4 }}>Loading...</Typography>;
    if (error) return <Typography variant="h5" color="error" sx={{ p: 4 }}>{error}</Typography>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
            {/* Header row */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {getGreeting()}
                        {user.firstName ? `, ${user.firstName}` : ''}!{' '}
                        <span role="img" aria-label="wave">👋</span>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Here's what's happening in your translation workspace.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Chip
                        icon={chipProps.icon}
                        label={`${chipProps.label} Plan`}
                        sx={{
                            px: 1.5,
                            fontSize: 14,
                            borderRadius: '14px',
                            ...chipProps.style,
                        }}
                    />
                    <QuickActionsMenu />
                </Box>
            </Box>

            {/* Onboarding checklist (collapsible) */}
            {!allComplete && (
                <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: '#f6fafe' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Get Started Checklist</Typography>
                        <IconButton
                            onClick={() => setChecklistOpen((v) => !v)}
                            size="small"
                            sx={{
                                transform: checklistOpen ? 'rotate(180deg)' : 'rotate(0)',
                                transition: '0.2s',
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </Box>
                    <Collapse in={checklistOpen}>
                        <Box sx={{ mt: 2 }}>
                            <Stepper activeStep={steps.filter((s) => s.complete).length} alternativeLabel>
                                {steps.map((step) => (
                                    <Step key={step.label} completed={step.complete}>
                                        <StepLabel>{step.label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <List>
                                {steps.map((step) => (
                                    <ListItem
                                        button
                                        key={step.label}
                                        onClick={step.action}
                                        sx={{ opacity: step.complete ? 0.5 : 1 }}
                                    >
                                        <ListItemIcon>{step.icon}</ListItemIcon>
                                        <ListItemText primary={step.label} />
                                        <Checkbox checked={step.complete} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Collapse>
                </Paper>
            )}

            {/* Alerts strip */}
            <AlertsStrip
                overdueCount={overdueCount}
                mtPercent={mtPercent}
                storagePercent={storagePercent}
                pendingInvites={pendingInvites}
            />

            {/* KPI cards */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<WorkOutlineIcon />}
                        label="Active projects"
                        value={activeProjects}
                        accent={DASHBOARD_COLORS.active}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<CheckCircleOutlineIcon />}
                        label="Completed projects"
                        value={completedProjects}
                        accent={DASHBOARD_COLORS.completed}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<TranslateIcon />}
                        label="Words translated"
                        value={wordsTranslated.toLocaleString()}
                        accent={DASHBOARD_COLORS.words}
                        subtext={monthDeltaSubtext}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<GroupIcon />}
                        label="Team members"
                        value={team.length}
                        accent={DASHBOARD_COLORS.team}
                        subtext={pendingInvites > 0 ? `${pendingInvites} pending` : 'all active'}
                    />
                </Grid>
            </Grid>

            {/* Charts row */}
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={7}>
                    <ActivityChart data={wordsByDay} />
                </Grid>
                <Grid item xs={12} md={5}>
                    <TopLanguagesChart languages={topLanguages} />
                </Grid>
            </Grid>

            {/* Recent projects */}
            <Box sx={{ mt: SECTION_GAP }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Recent projects
                    </Typography>
                    <Button onClick={() => navigate('/dashboard/projects')} sx={{ textTransform: 'none' }}>
                        View all
                    </Button>
                </Box>
                {recentProjects.length > 0 ? (
                    <Grid container spacing={3} alignItems="stretch">
                        {recentProjects.map((project) => (
                            <RecentProjectCard key={project._id} project={project} token={token} />
                        ))}
                    </Grid>
                ) : (
                    <Paper
                        sx={{
                            p: 5,
                            borderRadius: '16px',
                            textAlign: 'center',
                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
                        }}
                    >
                        <WorkOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="h6" gutterBottom>
                            No projects yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Create your first project to start tracking translation progress.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddCircleIcon />}
                            onClick={() => navigate('/dashboard/projects/new')}
                        >
                            Create project
                        </Button>
                    </Paper>
                )}
            </Box>

            {/* Usage cards */}
            <Box sx={{ mt: SECTION_GAP }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Plan usage
                </Typography>
                <UsageCards
                    charUsed={usage.character_count}
                    charLimit={charLimit}
                    storageUsed={usage.storage_used}
                    storageLimit={storageLimit}
                />
            </Box>
        </Container>
    );
};

export default DashboardHome;
